import { createAction } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import { getBusinessEmailsFetchStatus } from 'apps/email/selectors/emailSelectors'
import { FetchStatus } from 'apps/sms/smsInterfaces'
import { alert } from 'common/core'
import * as uiActions from 'common/core/actions/uiActions'
import { ChannelType } from 'common/core/constants/ChannelType'
import { IThunkAction } from 'common/core/interfaces/actions'
import { getPermission, PermissionTargets } from 'common/userRoles'
import { settingsApi } from 'shared/api/requests/settings'
import { createAsyncAction } from 'shared/lib/redux'

import { ConnectBusinessEmailSteps } from '../emailConstants'

export const openModalConnectEmailCountry = (): IThunkAction => (dispatch, getState) => {
  const state = getState()
  const hasPermission = getPermission(state, PermissionTargets.CAN_EDIT_EMAIL_CHANNEL)
  if (!hasPermission) {
    alert(l.translate('You don’t have rights to change it. Please ask Admin to do this.'), 'danger')
    return
  }
  return dispatch(uiActions.openCompactSettingsModal(ChannelType.EMAIL))
}

export const getBusinessEmails = createAsyncAction(
  'email/getBusinessEmails',
  async () => {
    const response = await settingsApi.email.getBusinessEmails()
    return response.data
  },
  {
    condition: (safeFetch: boolean = false, { getState }) => {
      const fetchStatus = getBusinessEmailsFetchStatus(getState())
      return safeFetch && fetchStatus === FetchStatus.NOT_FETCHED
    },
  },
)

export const addBusinessEmail = createAsyncAction(
  'email/addBusinessEmail',
  async ({
    email,
    senderName,
    replyTo = '',
    isPreferred = false,
  }: {
    email: string
    senderName: string
    replyTo: string
    isPreferred: boolean
  }) => {
    const response = await settingsApi.email.addBusinessEmail({
      body: {
        email,
        sender_name: senderName,
        reply_to: replyTo,
        is_default: isPreferred,
      },
    })
    return response.data
  },
  {
    autoHandleByGuards: [settingsApi.email.addBusinessEmail.isInvalidEmailError],
  },
)

export const updateBusinessEmail = createAsyncAction(
  'email/updateBusinessEmail',
  async ({
    emailId,
    senderName,
    replyTo,
    isPreferred = false,
  }: {
    emailId: number | null
    senderName: string | null
    replyTo: string | null
    isPreferred: boolean
  }) => {
    const response = await settingsApi.email.updateBusinessEmail({
      body: {
        email_id: emailId,
        sender_name: senderName,
        reply_to: replyTo,
        is_default: isPreferred,
      },
    })
    return response.data
  },
  {
    autoHandleByGuards: [settingsApi.email.updateBusinessEmail.isInvalidEmailError],
  },
)

export const verifyBusinessDomain = createAsyncAction(
  'email/verifyBusinessDomain',
  async (domainId: number) => {
    const response = await settingsApi.email.verifyBusinessDomain({
      body: { domain_id: domainId },
    })
    return response.data
  },
)

export const removeBusinessEmail = createAsyncAction(
  'email/removeBusinessEmail',
  async (email: string) => {
    const response = await settingsApi.email.removeBusinessEmail({ body: { email } })
    return response.data
  },
)

export const setConnectBusinessEmailStep = createAction<{
  step: ConnectBusinessEmailSteps | null
  emailId: number | string | null
} | null>(`email/setConnectBusinessEmailStep`)
